import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Alert,
  Spin,
  Button,
  Typography,
  Space,
  Badge,
  Progress,
  Collapse,
  Divider,
  Tooltip,
  message
} from 'antd';
import {
  TagOutlined,
  DollarOutlined,
  AppstoreOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  PrinterOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { dashboardService } from '../services/api';
import KPIsCategorizasPrint from '../components/print/KPIsCategorizasPrint';

const { Title, Text } = Typography;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2
  }).format(amount || 0);

function getStockColor(stockActual, stockMinimo) {
  const actual = parseFloat(stockActual) || 0;
  const minimo = parseFloat(stockMinimo) || 0;
  if (actual === 0) return '#ff4d4f';
  if (actual <= minimo) return '#ff4d4f';
  if (actual <= minimo * 1.5) return '#faad14';
  return '#52c41a';
}

function KPIsCategorias() {
  const [data, setData]             = useState([]);
  const [valorTotal, setValorTotal] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [printLoading, setPrintLoading] = useState(false);

  const printRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardService.getKPIsCategorias();
      setData(res.data.data || []);
      setValorTotal(res.data.valor_total_inventario || 0);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar KPIs por categoría');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'KPIs por Categoría - Inventario',
    pageStyle: `@page { size: A4 landscape; margin: 1cm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
    onBeforePrint: () => setPrintLoading(true),
    onAfterPrint: () => setPrintLoading(false),
  });

  // ─── Summary stats ───────────────────────────────────────────
  const totalCategorias = data.length;
  const totalProductos = data.reduce((acc, cat) => acc + (parseInt(cat.total_productos) || 0), 0);
  const categoriasConStockBajo = data.filter((cat) => (parseInt(cat.productos_bajo_stock) || 0) > 0).length;

  // ─── Mini product table columns (inside collapse) ────────────
  const productColumns = [
    {
      title: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 110,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text>
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      ellipsis: true
    },
    {
      title: 'Stock Actual',
      dataIndex: 'stock_actual',
      key: 'stock_actual',
      width: 110,
      align: 'right',
      render: (val, record) => (
        <Text strong style={{ color: getStockColor(val, record.stock_minimo) }}>
          {parseFloat(val).toLocaleString('es-MX')} {record.unidad_medida}
        </Text>
      )
    },
    {
      title: 'Stock Mín.',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
      width: 100,
      align: 'right',
      render: (val, record) =>
        `${parseFloat(val).toLocaleString('es-MX')} ${record.unidad_medida}`
    },
    {
      title: 'P. Compra',
      dataIndex: 'precio_compra',
      key: 'precio_compra',
      width: 120,
      align: 'right',
      render: (val) => formatCurrency(val)
    }
  ];

  // ─── Comparison table columns ────────────────────────────────
  const comparisonColumns = [
    {
      title: 'Categoría',
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      render: (nombre) => (
        <Space>
          <TagOutlined style={{ color: '#1677ff' }} />
          <Text strong>{nombre}</Text>
        </Space>
      )
    },
    {
      title: 'Productos',
      dataIndex: 'total_productos',
      key: 'total_productos',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.total_productos - b.total_productos,
      render: (val) => <Tag color="blue">{val}</Tag>
    },
    {
      title: 'Valor Inventario',
      dataIndex: 'valor_inventario',
      key: 'valor_inventario',
      width: 150,
      align: 'right',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.valor_inventario - b.valor_inventario,
      render: (val) => (
        <Text strong style={{ color: '#722ed1' }}>{formatCurrency(val)}</Text>
      )
    },
    {
      title: '% Inventario',
      dataIndex: 'porcentaje_valor',
      key: 'porcentaje_valor',
      width: 130,
      align: 'center',
      sorter: (a, b) => a.porcentaje_valor - b.porcentaje_valor,
      render: (val) => (
        <Tooltip title={`${val}% del inventario total`}>
          <Progress
            percent={val}
            size="small"
            strokeColor="#722ed1"
            format={(p) => `${p}%`}
          />
        </Tooltip>
      )
    },
    {
      title: 'Bajo Stock',
      dataIndex: 'productos_bajo_stock',
      key: 'productos_bajo_stock',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.productos_bajo_stock - b.productos_bajo_stock,
      render: (val) =>
        val > 0 ? (
          <Tag color="orange" icon={<ExclamationCircleOutlined />}>{val}</Tag>
        ) : (
          <Tag color="success">0</Tag>
        )
    },
    {
      title: 'Sin Stock',
      dataIndex: 'productos_sin_stock',
      key: 'productos_sin_stock',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.productos_sin_stock - b.productos_sin_stock,
      render: (val) =>
        val > 0 ? (
          <Tag color="red" icon={<ExclamationCircleOutlined />}>{val}</Tag>
        ) : (
          <Tag color="success">0</Tag>
        )
    },
    {
      title: 'Entradas Mes',
      dataIndex: 'total_entradas',
      key: 'total_entradas',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.total_entradas - b.total_entradas,
      render: (val) => (
        <Text style={{ color: '#52c41a', fontWeight: 600 }}>
          <ArrowUpOutlined /> {parseFloat(val || 0).toLocaleString('es-MX')}
        </Text>
      )
    },
    {
      title: 'Salidas Mes',
      dataIndex: 'total_salidas',
      key: 'total_salidas',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.total_salidas - b.total_salidas,
      render: (val) => (
        <Text style={{ color: '#ff4d4f', fontWeight: 600 }}>
          <ArrowDownOutlined /> {parseFloat(val || 0).toLocaleString('es-MX')}
        </Text>
      )
    },
    {
      title: 'Valor Entradas',
      dataIndex: 'entradas_valor',
      key: 'entradas_valor',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.entradas_valor - b.entradas_valor,
      render: (val) => (
        <Text style={{ color: '#52c41a' }}>{formatCurrency(val)}</Text>
      )
    },
    {
      title: 'Valor Salidas',
      dataIndex: 'salidas_valor',
      key: 'salidas_valor',
      width: 140,
      align: 'right',
      sorter: (a, b) => a.salidas_valor - b.salidas_valor,
      render: (val) => (
        <Text style={{ color: '#ff4d4f' }}>{formatCurrency(val)}</Text>
      )
    }
  ];

  // Totals for summary row
  const summaryTotals = {
    total_productos:      data.reduce((s, c) => s + (parseInt(c.total_productos) || 0), 0),
    valor_inventario:     data.reduce((s, c) => s + (parseFloat(c.valor_inventario) || 0), 0),
    productos_bajo_stock: data.reduce((s, c) => s + (parseInt(c.productos_bajo_stock) || 0), 0),
    productos_sin_stock:  data.reduce((s, c) => s + (parseInt(c.productos_sin_stock) || 0), 0),
    entradas_qty:         data.reduce((s, c) => s + (parseFloat(c.total_entradas) || 0), 0),
    salidas_qty:          data.reduce((s, c) => s + (parseFloat(c.total_salidas) || 0), 0),
    entradas_valor:       data.reduce((s, c) => s + (parseFloat(c.entradas_valor) || 0), 0),
    salidas_valor:        data.reduce((s, c) => s + (parseFloat(c.salidas_valor) || 0), 0)
  };

  // ─── Render ──────────────────────────────────────────────────
  return (
    <div>
      {/* ── Hidden print component ───────────────────────────────────────────── */}
      <div style={{ display: 'none' }}>
        <KPIsCategorizasPrint ref={printRef} data={data} valorTotal={valorTotal} />
      </div>

      {/* ── Screen header ──────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <BarChartOutlined style={{ marginRight: 8, color: '#1677ff' }} />
            KPIs por Categoría
          </Title>
          <Text type="secondary">
            Análisis detallado por categoría · {dayjs().format('MMMM YYYY')}
          </Text>
        </div>
        <Space>
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={printLoading}
          >
            Imprimir
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
            type="primary"
          >
            Actualizar
          </Button>
        </Space>
      </div>

      {/* ── Screen content ─────────────────────────────────────── */}
      <Spin spinning={loading} tip="Cargando datos...">
        {/* Summary KPI cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Total Categorías"
                value={totalCategorias}
                prefix={<TagOutlined style={{ color: '#1677ff' }} />}
                valueStyle={{ color: '#1677ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Valor Total Inventario"
                value={valorTotal}
                prefix={<DollarOutlined style={{ color: '#722ed1' }} />}
                formatter={(val) => formatCurrency(val)}
                valueStyle={{ color: '#722ed1', fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Total Productos Activos"
                value={totalProductos}
                prefix={<AppstoreOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card kpi-card" bordered={false}>
              <Statistic
                title="Categorías con Stock Bajo"
                value={categoriasConStockBajo}
                prefix={<ExclamationCircleOutlined style={{ color: categoriasConStockBajo > 0 ? '#fa8c16' : '#52c41a' }} />}
                valueStyle={{ color: categoriasConStockBajo > 0 ? '#fa8c16' : '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Category cards grid */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {data.map((cat) => (
            <Col key={cat.id} xs={24} sm={24} md={12} xl={8}>
              <Card
                bordered={false}
                style={{ height: '100%' }}
                title={
                  <Space>
                    <TagOutlined style={{ color: '#1677ff' }} />
                    <Text strong>{cat.nombre}</Text>
                    <Badge
                      count={cat.total_productos}
                      style={{ backgroundColor: '#1677ff' }}
                      overflowCount={999}
                    />
                  </Space>
                }
                extra={
                  cat.num_movimientos > 0 ? (
                    <Tag color="blue" style={{ fontSize: 11 }}>
                      {cat.num_movimientos} mov. este mes
                    </Tag>
                  ) : (
                    <Tag color="default" style={{ fontSize: 11 }}>
                      Sin mov. este mes
                    </Tag>
                  )
                }
              >
                {/* Stats rows */}
                <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
                  <Col span={12}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>
                      Valor Inventario
                    </div>
                    <Text strong style={{ color: '#722ed1', fontSize: 14 }}>
                      {formatCurrency(cat.valor_inventario)}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>
                      % del Total
                    </div>
                    <Tooltip title={`${cat.porcentaje_valor}% del inventario total`}>
                      <Progress
                        percent={cat.porcentaje_valor}
                        size="small"
                        strokeColor="#722ed1"
                        format={(p) => `${p}%`}
                        style={{ marginBottom: 0 }}
                      />
                    </Tooltip>
                  </Col>
                </Row>

                <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
                  <Col span={12}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>
                      <ArrowUpOutlined style={{ color: '#52c41a' }} /> Entradas
                    </div>
                    <Text strong style={{ color: '#52c41a' }}>
                      {parseFloat(cat.total_entradas || 0).toLocaleString('es-MX')} uds.
                    </Text>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>
                      <ArrowDownOutlined style={{ color: '#ff4d4f' }} /> Salidas
                    </div>
                    <Text strong style={{ color: '#ff4d4f' }}>
                      {parseFloat(cat.total_salidas || 0).toLocaleString('es-MX')} uds.
                    </Text>
                  </Col>
                </Row>

                {/* Stock warning */}
                {cat.productos_bajo_stock > 0 && (
                  <Alert
                    type="warning"
                    message={`${cat.productos_bajo_stock} producto(s) con stock bajo`}
                    style={{ marginBottom: 12, padding: '4px 10px', fontSize: 12 }}
                    showIcon={false}
                  />
                )}

                <Divider style={{ margin: '8px 0' }} />

                {/* Top producto */}
                {cat.top_producto ? (
                  <div style={{ marginBottom: 10 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Más activo este mes: </Text>
                    <Tag color="geekblue" style={{ fontSize: 11 }}>
                      {cat.top_producto.clave} — {cat.top_producto.nombre} ({cat.top_producto.total_movimientos || 0} mov.)
                    </Tag>
                  </div>
                ) : (
                  <div style={{ marginBottom: 10 }}>
                    <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                      Sin movimientos este mes
                    </Text>
                  </div>
                )}

                {/* Product drill-down */}
                {cat.productos && cat.productos.length > 0 && (
                  <Collapse
                    size="small"
                    ghost
                    items={[
                      {
                        key: '1',
                        label: (
                          <Text style={{ fontSize: 12 }}>
                            Ver productos ({cat.productos.length})
                          </Text>
                        ),
                        children: (
                          <Table
                            dataSource={cat.productos}
                            columns={productColumns}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            scroll={{ x: 500 }}
                            locale={{ emptyText: 'Sin productos' }}
                          />
                        )
                      }
                    ]}
                  />
                )}

                {cat.total_productos === 0 && (
                  <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                    Sin productos en esta categoría
                  </Text>
                )}
              </Card>
            </Col>
          ))}
        </Row>

        {/* Comparison table */}
        {data.length > 0 && (
          <Card
            bordered={false}
            title={
              <Space>
                <BarChartOutlined style={{ color: '#1677ff' }} />
                <span>Resumen Comparativo por Categoría</span>
              </Space>
            }
          >
            <Table
              dataSource={data}
              columns={comparisonColumns}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: 1200 }}
              locale={{ emptyText: 'Sin datos de categorías' }}
              rowClassName={(_, index) => (index % 2 === 0 ? '' : 'ant-table-row-striped')}
              summary={() => (
                <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                  <Table.Summary.Cell index={0}>
                    <Text strong>TOTALES</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <Text strong>{summaryTotals.total_productos}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong style={{ color: '#722ed1' }}>
                      {formatCurrency(summaryTotals.valor_inventario)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="center">
                    <Text strong>100%</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="center">
                    <Text strong style={{ color: summaryTotals.productos_bajo_stock > 0 ? '#fa8c16' : undefined }}>
                      {summaryTotals.productos_bajo_stock}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="center">
                    <Text strong style={{ color: summaryTotals.productos_sin_stock > 0 ? '#ff4d4f' : undefined }}>
                      {summaryTotals.productos_sin_stock}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <Text strong style={{ color: '#52c41a' }}>
                      {parseFloat(summaryTotals.entradas_qty).toLocaleString('es-MX')}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <Text strong style={{ color: '#ff4d4f' }}>
                      {parseFloat(summaryTotals.salidas_qty).toLocaleString('es-MX')}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    <Text strong style={{ color: '#52c41a' }}>
                      {formatCurrency(summaryTotals.entradas_valor)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    <Text strong style={{ color: '#ff4d4f' }}>
                      {formatCurrency(summaryTotals.salidas_valor)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        )}
      </Spin>
    </div>
  );
}

export default KPIsCategorias;
